import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, RotateCcw, ArrowRight, Lock, MailCheck, MailWarning, Compass, FileText } from "lucide-react";
import { toast } from "sonner";
import { getUser, clearAuth, authFetch } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import { PERSONA_META, useLang, LangToggle } from "@/components/landing/i18n";
import { downloadDiscoveryPdf } from "@/lib/downloads";

const RPT_UI = {
  en: {
    gateTitle: "Sign in to see your reports",
    gateBtn: "Sign in / Create free account",
    title: "My reports",
    sub: "Every full battery you've completed, newest first.",
    compare: "Compare batteries",
    retake: "Retake the battery",
    empty: "No full reports yet — your first battery takes about 15 minutes.",
    startNow: "Start now",
    viewCard: "View card",
    emailed: "Emailed",
    emailPending: "Email pending",
    discoveryTitle: "Discovery profiles",
    discoverySub: "Five-module psychometric reports, newest first.",
    viewProgress: "View progress",
    topCluster: "Top cluster",
    fit: "fit",
    viewProfile: "View profile",
    professional: "Professional",
    student: "Student",
    pdfOk: "Discovery PDF downloaded",
    pdfFail: "PDF download failed",
    dateLocale: "en-GB",
  },
  hi: {
    gateTitle: "अपनी रिपोर्ट देखने के लिए साइन इन करें",
    gateBtn: "साइन इन / मुफ़्त खाता बनाएँ",
    title: "मेरी रिपोर्ट",
    sub: "आपकी हर पूरी बैटरी, नई से पुरानी।",
    compare: "बैटरियों की तुलना करें",
    retake: "बैटरी फिर से दें",
    empty: "अभी कोई पूरी रिपोर्ट नहीं — पहली बैटरी में लगभग 15 मिनट लगते हैं।",
    startNow: "अभी शुरू करें",
    viewCard: "कार्ड देखें",
    emailed: "ईमेल हो गई",
    emailPending: "ईमेल लंबित",
    discoveryTitle: "डिस्कवरी प्रोफ़ाइल",
    discoverySub: "पाँच-मॉड्यूल साइकोमेट्रिक रिपोर्ट, नई से पुरानी।",
    viewProgress: "प्रगति देखें",
    topCluster: "टॉप क्लस्टर",
    fit: "फिट",
    viewProfile: "प्रोफ़ाइल देखें",
    professional: "प्रोफेशनल",
    student: "विद्यार्थी",
    pdfOk: "डिस्कवरी PDF डाउनलोड हुई",
    pdfFail: "PDF डाउनलोड विफल",
    dateLocale: "hi-IN",
  },
};

export default function MyReports() {
  const { lang } = useLang();
  const rui = RPT_UI[lang] || RPT_UI.en;
  const navigate = useNavigate();
  const [user, setUser] = useState(getUser());
  const [authOpen, setAuthOpen] = useState(false);
  const [reports, setReports] = useState(null);
  const [discoveryReports, setDiscoveryReports] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([authFetch("/api/my/reports"), authFetch(`/api/discovery/reports?lang=${lang}`)]).then(async ([r, dr]) => {
      if (r.status === 401 || dr.status === 401) {
        clearAuth();
        setUser(null);
        return;
      }
      setReports(await r.json());
      setDiscoveryReports(dr.ok ? await dr.json() : []);
    });
  }, [user, lang]);

  const retake = async () => {
    setBusy(true);
    try {
      await authFetch("/api/full/retake", { method: "POST" });
      localStorage.setItem("biq_full_start", String(Date.now()));
      navigate("/assessment");
    } catch {
      setBusy(false);
    }
  };

  const downloadDiscovery = async (report) => {
    setBusy(true);
    try {
      await downloadDiscoveryPdf(report.id, undefined, lang);
      toast.success(rui.pdfOk);
    } catch (err) {
      toast.error(err.message || rui.pdfFail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="reports-page">
      <header className="max-w-4xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="reports-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <LangToggle />
          {user && (
            <span className="text-sm font-semibold text-ink/60" data-testid="reports-user-chip">{user.name.split(" ")[0]}</span>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-14">
        {!user && (
          <div className="rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="reports-gate">
            <span className="inline-flex h-14 w-14 rounded-2xl bg-iqblue/10 items-center justify-center">
              <Lock className="h-6 w-6 text-iqblue" />
            </span>
            <h1 className="font-display font-bold text-3xl tracking-tight mt-6">{rui.gateTitle}</h1>
            <button onClick={() => setAuthOpen(true)} data-testid="reports-signin-button" className="mt-8 bg-ink text-paper font-semibold px-8 py-4 rounded-full hover:bg-iqblue transition-colors duration-300">
              {rui.gateBtn}
            </button>
          </div>
        )}

        {user && !reports && <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-iqblue" data-testid="reports-loading" /></div>}

        {user && reports && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{rui.title}</h1>
                <p className="mt-2 text-sm text-ink/55">{rui.sub}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                {new Set(reports.map((r) => r.track || "student")).size > 1 && (
                  <Link to="/compare" data-testid="reports-compare-link" className="flex items-center gap-2 border border-iqpurple/30 text-iqpurple font-semibold px-6 py-3.5 rounded-full hover:bg-iqpurple hover:text-paper transition-colors duration-300">
                    {rui.compare}
                  </Link>                )}
                <button onClick={retake} disabled={busy} data-testid="reports-retake-button" className="flex items-center gap-2 bg-ink text-paper font-semibold px-6 py-3.5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors duration-300">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} {rui.retake}
                </button>
              </div>
            </div>

            {reports.length === 0 && (
              <div className="mt-10 rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="reports-empty">
                <p className="text-ink/60">{rui.empty}</p>
                <button onClick={retake} data-testid="reports-start-button" className="mt-6 inline-flex items-center gap-2 bg-iqblue text-paper font-semibold px-7 py-3.5 rounded-full hover:bg-ink transition-colors">
                  {rui.startNow} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {reports.map((r, i) => {
                const meta = PERSONA_META[r.persona];
                const txt = meta[lang] || meta.en;
                return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="rounded-3xl bg-cream border border-ink/10 p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_25px_50px_-20px_rgba(10,22,51,0.2)]"
                    data-testid={`report-card-${i}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-display font-bold text-2xl tracking-tight" style={{ color: meta.color }}>{txt.name}</div>
                        <div className="text-xs text-ink/50 mt-1 flex items-center gap-2">
                          {new Date(r.created_at).toLocaleDateString(rui.dateLocale, { day: "2-digit", month: "short", year: "numeric" })}
                          <span className="rounded-full border border-ink/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">{(r.track || "student") === "professional" ? rui.professional : rui.student}</span>
                        </div>
                      </div>
                      <span className="text-xs font-bold rounded-full bg-ink text-paper px-3 py-1.5">{r.aptPct}%</span>
                    </div>
                    <p className="mt-4 text-sm text-ink/60 leading-relaxed line-clamp-2">{txt.blurb}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Link to={`/r/${r.id}`} data-testid={`report-view-${i}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-iqblue hover:gap-2.5 transition-all">
                          {rui.viewCard} <ArrowRight className="h-4 w-4" />
                        </Link>
                        <button
                          data-testid={`report-pdf-${i}`}
                          onClick={async () => {
                            try {
                              const res = await authFetch(`/api/reports/${r.id}/pdf?lang=${lang}`);
                              if (!res.ok) throw new Error();
                              const blob = await res.blob();
                              const a = document.createElement("a");
                              a.href = URL.createObjectURL(blob);
                              a.download = "brightiq-report.pdf";
                              a.click();
                              URL.revokeObjectURL(a.href);
                            } catch {
                              toast.error(rui.pdfFail);
                            }
                          }}
                          className="text-sm font-semibold text-ink/55 hover:text-ink transition-colors"
                        >
                          ↓ PDF
                        </button>
                      </div>
                      {r.email_sent
                        ? <span className="flex items-center gap-1 text-[11px] font-semibold text-iqgreen"><MailCheck className="h-3.5 w-3.5" /> {rui.emailed}</span>
                        : <span className="flex items-center gap-1 text-[11px] font-semibold text-ink/40"><MailWarning className="h-3.5 w-3.5" /> {rui.emailPending}</span>}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {discoveryReports.length > 0 && (
              <div className="mt-12" data-testid="discovery-reports-section">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 rounded-2xl bg-iqamber/15 items-center justify-center"><Compass className="h-5 w-5 text-iqamber" /></span>
                    <div>
                      <h2 className="font-display font-bold text-2xl tracking-tight">{rui.discoveryTitle}</h2>
                      <p className="text-xs text-ink/50 mt-0.5">{rui.discoverySub}</p>
                    </div>
                  </div>
                  {discoveryReports.length > 1 && <Link to="/progress" data-testid="discovery-progress-link" className="text-sm font-semibold text-iqblue">{rui.viewProgress}</Link>}
                </div>
                <div className="mt-5 grid sm:grid-cols-2 gap-5">
                  {discoveryReports.map((r, i) => (
                    <motion.div key={r.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.08 }} className="rounded-3xl bg-cream border border-ink/10 p-7" data-testid={`discovery-report-card-${i}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-display font-bold text-xl tracking-tight">{r.archetype}</div>
                          <div className="text-xs text-ink/50 mt-1">{new Date(r.created_at).toLocaleDateString(rui.dateLocale, { day: "2-digit", month: "short", year: "numeric" })}</div>
                        </div>
                        <span className="text-xs font-bold rounded-full bg-iqamber text-ink px-3 py-1.5">{r.aptitude_overall}%</span>
                      </div>
                      <p className="mt-4 text-sm text-ink/60">{rui.topCluster}: <b className="text-ink">{r.clusters?.[0]?.name}</b> · {r.clusters?.[0]?.fit}% {rui.fit}</p>
                      <div className="mt-5 flex flex-wrap items-center gap-4">
                        <Link to={`/discovery/report/${r.id}`} data-testid={`discovery-report-view-${i}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-iqblue hover:gap-2.5 transition-all">
                          {rui.viewProfile} <ArrowRight className="h-4 w-4" />
                        </Link>
                        <button onClick={() => downloadDiscovery(r)} disabled={busy} data-testid={`discovery-report-pdf-${i}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-iqamber hover:gap-2.5 disabled:opacity-60 transition-all">
                          <FileText className="h-4 w-4" /> PDF
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={(u) => setUser(u)} />
    </div>
  );
}
